import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getLocalApiUrl } from '../config/api'

const SOF_OPTIONS = [
  'Salary / wages',
  'Business income',
  'Savings',
  'Gambling winnings',
  'Asset sale',
  'Inheritance',
  'Other',
]

const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[15px] text-slate-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20'

export default function PatronSowOutreachForm() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const isPreview =
    searchParams.get('preview') === '1' ||
    searchParams.get('preview') === 'true' ||
    searchParams.get('preview') === 'yes'

  const [validating, setValidating] = useState(true)
  const [valid, setValid] = useState(false)
  const [meta, setMeta] = useState(null)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [doneKind, setDoneKind] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [mode, setMode] = useState('sow') // sow | meeting

  const [sofCat, setSofCat] = useState('')
  const [sofOtherDetail, setSofOtherDetail] = useState('')
  const [sowCat, setSowCat] = useState('')
  const [occupation, setOccupation] = useState('')
  const [employer, setEmployer] = useState('')
  const [narrative, setNarrative] = useState('')

  const [preference, setPreference] = useState('callback')
  const [preferredTimes, setPreferredTimes] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [meetingNotes, setMeetingNotes] = useState('')

  useEffect(() => {
    let cancelled = false
    async function validate() {
      if (isPreview) {
        setValidating(false)
        setValid(true)
        setMeta({
          patronName: 'Patron (preview)',
          dueAt: new Date(Date.now() + 5 * 86400000).toISOString(),
          preview: true,
        })
        return
      }
      if (!token) {
        setValidating(false)
        setValid(false)
        setError('This link is missing a token.')
        return
      }
      try {
        const res = await fetch(
          getLocalApiUrl(`/api/public/outreach/sow?token=${encodeURIComponent(token)}`)
        )
        const j = await res.json()
        if (cancelled) return
        if (!res.ok || !j.valid) {
          setValid(false)
          setError(
            j.error === 'already_completed'
              ? 'This form has already been submitted. Thank you.'
              : j.error === 'cancelled'
                ? 'This request was cancelled.'
                : 'This link is invalid or has expired.'
          )
          return
        }
        setValid(true)
        setMeta(j.data || {})
      } catch (_) {
        if (!cancelled) {
          setValid(false)
          setError('Unable to open this form. Please try again later.')
        }
      } finally {
        if (!cancelled) setValidating(false)
      }
    }
    validate()
    return () => {
      cancelled = true
    }
  }, [token, isPreview])

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (isPreview) {
      setError('This is a staff preview — submissions are disabled. Send a request to generate a live patron link.')
      return
    }
    setSubmitting(true)
    try {
      const body =
        mode === 'meeting'
          ? {
              token,
              responseKind: 'meeting',
              preference,
              preferredTimes,
              contactPhone,
              notes: meetingNotes,
            }
          : {
              token,
              responseKind: 'sow',
              sofCat: sofCat === 'Other' ? 'other' : sofCat,
              sofOtherDetail: sofCat === 'Other' || sofCat === 'other' ? sofOtherDetail : '',
              sowCat,
              occupation,
              employer,
              narrative,
            }

      if (mode === 'sow' && !sofCat && !sowCat) {
        throw new Error('Please select a source of funds or source of wealth')
      }
      if (mode === 'sow' && (sofCat === 'Other' || sofCat === 'other') && !sofOtherDetail.trim()) {
        throw new Error('Please describe the other source of funds')
      }

      const res = await fetch(getLocalApiUrl('/api/public/outreach/sow'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Submit failed')
      setDone(true)
      setDoneKind(mode)
    } catch (err) {
      setError(err.message || 'Submit failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-teal-50/40 to-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-6 text-center">
          <div className="text-xs font-semibold tracking-[0.14em] uppercase text-teal-800/80">PaySafeGO</div>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">Source of Wealth</h1>
          <p className="mt-1 text-sm text-slate-600">
            Please provide the information requested, or ask for a meeting with our compliance team.
          </p>
        </div>

        {isPreview && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-950">
            <strong className="font-semibold">Staff preview</strong>
            {' — '}
            This is how the patron form looks. Submissions are disabled; send a request to create a live link.
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 sm:p-6">
          {validating && <p className="text-sm text-slate-500 m-0">Checking your link…</p>}

          {!validating && !valid && !done && (
            <p className="text-sm text-slate-700 m-0">{error || 'Unable to open this form.'}</p>
          )}

          {done && (
            <div className="text-center py-4">
              <div className="text-lg font-semibold text-teal-800">Thank you</div>
              <p className="text-sm text-slate-600 mt-2 mb-0">
                {doneKind === 'meeting'
                  ? 'Your meeting request has been received. Our team will be in touch.'
                  : 'Your Source of Wealth information has been submitted.'}
              </p>
            </div>
          )}

          {!validating && valid && !done && (
            <form onSubmit={submit} className="space-y-4">
              {meta?.patronName && (
                <p className="text-sm text-slate-600 m-0">
                  Request for <span className="font-medium text-slate-900">{meta.patronName}</span>
                  {meta.dueAt && (
                    <span className="text-slate-500">
                      {' '}
                      · please respond by{' '}
                      {new Date(meta.dueAt).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                </p>
              )}

              <div className="flex rounded-lg border border-slate-200 p-1 bg-slate-50">
                <button
                  type="button"
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium ${
                    mode === 'sow' ? 'bg-white shadow-sm text-teal-900' : 'text-slate-600'
                  }`}
                  onClick={() => setMode('sow')}
                >
                  Fill form
                </button>
                <button
                  type="button"
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium ${
                    mode === 'meeting' ? 'bg-white shadow-sm text-teal-900' : 'text-slate-600'
                  }`}
                  onClick={() => setMode('meeting')}
                >
                  Request a meeting
                </button>
              </div>

              {mode === 'sow' ? (
                <>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Source of funds</span>
                    <select className={`${inputClass} mt-1`} value={sofCat} onChange={(e) => setSofCat(e.target.value)}>
                      <option value="">Select…</option>
                      {SOF_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>
                  {(sofCat === 'Other' || sofCat === 'other') && (
                    <label className="block">
                      <span className="text-sm font-medium text-slate-800">Please describe</span>
                      <input
                        className={`${inputClass} mt-1`}
                        value={sofOtherDetail}
                        onChange={(e) => setSofOtherDetail(e.target.value)}
                      />
                    </label>
                  )}
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Source of wealth</span>
                    <select className={`${inputClass} mt-1`} value={sowCat} onChange={(e) => setSowCat(e.target.value)}>
                      <option value="">Select…</option>
                      {SOF_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Occupation</span>
                    <input
                      className={`${inputClass} mt-1`}
                      value={occupation}
                      onChange={(e) => setOccupation(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Employer</span>
                    <input
                      className={`${inputClass} mt-1`}
                      value={employer}
                      onChange={(e) => setEmployer(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Additional details</span>
                    <textarea
                      className={`${inputClass} mt-1 min-h-[96px] resize-y`}
                      value={narrative}
                      onChange={(e) => setNarrative(e.target.value)}
                      placeholder="Any further information that helps explain your source of funds or wealth"
                    />
                  </label>
                </>
              ) : (
                <>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Preferred contact</span>
                    <select
                      className={`${inputClass} mt-1`}
                      value={preference}
                      onChange={(e) => setPreference(e.target.value)}
                    >
                      <option value="callback">Phone callback</option>
                      <option value="in_person">In person at the venue</option>
                      <option value="video">Video call</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Best times</span>
                    <input
                      className={`${inputClass} mt-1`}
                      value={preferredTimes}
                      onChange={(e) => setPreferredTimes(e.target.value)}
                      placeholder="e.g. Weekdays after 2pm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Contact phone</span>
                    <input
                      className={`${inputClass} mt-1`}
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-slate-800">Notes</span>
                    <textarea
                      className={`${inputClass} mt-1 min-h-[80px] resize-y`}
                      value={meetingNotes}
                      onChange={(e) => setMeetingNotes(e.target.value)}
                    />
                  </label>
                </>
              )}

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || isPreview}
                className="w-full rounded-lg bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white font-semibold py-2.5 text-sm"
              >
                {isPreview
                  ? 'Preview only — cannot submit'
                  : submitting
                    ? 'Submitting…'
                    : mode === 'meeting'
                      ? 'Request meeting'
                      : 'Submit'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
